import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ledgerAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';
import { ProductBadge, ProductFilter } from '../../components/products';
import { nyTime } from '../../lib/nyTime';

const CURRENCIES = ['points', 'xp', 'cash', 'credit'];
const STATUSES = ['pending', 'approved', 'settled', 'voided'];

function fmtAmount(x) {
  const n = Number(x || 0);
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ActionModal({ entry, action, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await ledgerAPI.action(entry.id, action, note);
      toast.success(t('Done'));
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`${t(action)} — ${fmtAmount(entry.amount)} ${t(entry.currency)}`} onClose={onClose}>
      <p className="mb-3 text-sm text-mms-muted">
        {entry.rule_name} · {entry.member?.name || entry.member_mid}
      </p>
      {entry.status === 'settled' && action === 'void' && (
        <p className="mb-3 text-sm text-[var(--mms-warning)]">
          {t('Settled value cannot be unmade — this inserts a compensating reversal entry.')}
        </p>
      )}
      <Field label={t('Note (optional)')}>
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={run} disabled={busy} className={action === 'void' || action === 'deny' ? btnDanger : btnPrimary}>
          {busy ? t('Working…') : t(action)}
        </button>
      </div>
    </Modal>
  );
}

export default function LedgerPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ status: '', currency: '', member: '', product: '' });
  const [page, setPage] = useState(1);
  const [acting, setActing] = useState(null); // {entry, action}

  const load = useCallback(() => {
    ledgerAPI.list({ ...filters, page, per: 25 })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load the ledger')));
  }, [filters, page]);
  useEffect(load, [load]);

  const summary = data?.summary || {};
  const pages = Math.max(1, Math.ceil((data?.total || 0) / 25));

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">{t('Ledger')}</h1>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {CURRENCIES.map((c) => (
          <div key={c} className={`${panelCls} p-4`}>
            <div className="text-xs uppercase tracking-wide text-mms-faint">{t(c)}</div>
            <div className="mt-1 text-xl font-semibold mms-num">{fmtAmount(summary[c]?.available)}</div>
            <div className="mt-0.5 text-xs text-mms-muted mms-num">
              {t('pending')}: {fmtAmount(summary[c]?.pending)}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-3">
        <ProductFilter value={filters.product} onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, product: v })); }} />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={`${inputCls} !w-36`} value={filters.status}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}>
          <option value="">{t('All statuses')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
        </select>
        <select className={`${inputCls} !w-36`} value={filters.currency}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, currency: e.target.value })); }}>
          <option value="">{t('All currencies')}</option>
          {CURRENCIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
        </select>
        <input className={`${inputCls} !w-56`} placeholder={t('Search member…')} value={filters.member}
          onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, member: e.target.value })); }} />
      </div>

      {data === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : data.items.length === 0 ? (
        <EmptyState>{t('No ledger entries match.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('When')}</th>
                <th className="px-4 py-2.5 font-medium">{t('ID')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Member')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Product')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Rule')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Amount')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs text-mms-muted">
                    {nyTime(e.created_at)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-medium mms-num">
                    {e.member?.membership_id || (e.member?.membership_number ? '#' + e.member.membership_number : '—')}
                  </td>
                  <td className="px-4 py-2.5">
                    {e.member?.name || '—'}
                  </td>
                  <td className="px-4 py-2.5"><ProductBadge product={e.product} /></td>
                  <td className="px-4 py-2.5">
                    {e.rule_name || '—'}
                    {e.reversal_of ? <span className="ml-1 text-xs text-[var(--mms-warning)]">{t('reversal')}</span> : null}
                    {e.note ? <div className="text-xs text-mms-faint">{e.note}</div> : null}
                  </td>
                  <td className={`px-4 py-2.5 text-right mms-num ${e.amount < 0 ? 'text-[var(--mms-danger)]' : ''}`}>
                    {fmtAmount(e.amount)} <span className="text-xs text-mms-faint">{t(e.currency)}</span>
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={e.status} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {e.status === 'pending' && (
                      <>
                        <button onClick={() => setActing({ entry: e, action: 'approve' })} className={`${btnGhost} mr-1.5`}>{t('approve')}</button>
                        <button onClick={() => setActing({ entry: e, action: 'deny' })} className={btnDanger}>{t('deny')}</button>
                      </>
                    )}
                    {e.status === 'approved' && (
                      <>
                        <button onClick={() => setActing({ entry: e, action: 'settle' })} className={`${btnGhost} mr-1.5`}>{t('settle')}</button>
                        <button onClick={() => setActing({ entry: e, action: 'void' })} className={btnDanger}>{t('void')}</button>
                      </>
                    )}
                    {e.status === 'settled' && !e.reversal_of && (
                      <button onClick={() => setActing({ entry: e, action: 'void' })} className={btnDanger}>{t('void')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm text-mms-muted">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={btnGhost}>{t('Previous')}</button>
          <span className="mms-num">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className={btnGhost}>{t('Next')}</button>
        </div>
      )}

      {acting && (
        <ActionModal
          entry={acting.entry}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); load(); }}
        />
      )}
    </div>
  );
}
